import { type WritableDraft } from '@reduxjs/toolkit';
import md5 from 'md5';
import { type RuleTopLevelCondition } from '@bbkAdminComponents/experiences/audiences/edit-rule/rules-utils';
import { bbkApiSlice } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { type DateString } from '@bbkAdminRedux/rtkq/current-user.slice';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';
import { type EnumLike } from '@bbkAdminUtils/utility-types';

type Pagination = {
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
  has_more: boolean;
};

export enum OptimizerEntityType {
  OPTIMIZATION = 'OPTIMIZATION',
}

export enum OptimizationActionType {
  PLAN_CHANGE = 'PLAN_CHANGE',
  PLAN_PRICE_ADJUSTMENT = 'PLAN_PRICE_ADJUSTMENT',
}

export enum OptimizerJobType {
  EXPORT = 'EXPORT',
  SIMULATION = 'SIMULATION',
  EXECUTION = 'EXECUTION',
}

export enum OptimizerJobStatus {
  SUBMITTED = 'SUBMITTED',
  PENDING = 'PENDING',
  RUNNABLE = 'RUNNABLE',
  STARTING = 'STARTING',
  RUNNING = 'RUNNING',
  SUCCEEDED = 'SUCCEEDED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export type OptimizerJobStatusCategory = 'in_progress' | 'success' | 'error';

/**
 * Categorizes a job status into: in_progress, success, or error
 * - In progress: SUBMITTED, PENDING, RUNNABLE, STARTING, RUNNING
 * - Success: SUCCEEDED
 * - Error: FAILED, CANCELLED
 */
export const getOptimizerJobStatusCategory = (
  status: EnumLike<typeof OptimizerJobStatus> | undefined
): OptimizerJobStatusCategory | undefined => {
  if (!status) return undefined;

  switch (status) {
    case OptimizerJobStatus.SUBMITTED:
    case OptimizerJobStatus.PENDING:
    case OptimizerJobStatus.RUNNABLE:
    case OptimizerJobStatus.STARTING:
    case OptimizerJobStatus.RUNNING:
      return 'in_progress';
    case OptimizerJobStatus.SUCCEEDED:
      return 'success';
    case OptimizerJobStatus.FAILED:
    case OptimizerJobStatus.CANCELLED:
      return 'error';
    default:
      return undefined;
  }
};

type AbstractOptimizationAction = {
  type: EnumLike<typeof OptimizationActionType>;
  display_name: string;
  condition: RuleTopLevelCondition;
};

export enum OptimizationPriceAdjustmentType {
  INCREASE_BY_PERCENTAGE = 'INCREASE_BY_PERCENTAGE',
  INCREASE_BY_FIXED_AMOUNT = 'INCREASE_BY_FIXED_AMOUNT',
  DECREASE_BY_PERCENTAGE = 'DECREASE_BY_PERCENTAGE',
  DECREASE_BY_FIXED_AMOUNT = 'DECREASE_BY_FIXED_AMOUNT',
  SET_FIXED_PRICE = 'SET_FIXED_PRICE',
}

type OptimizationActionPriceAdjustment = AbstractOptimizationAction & {
  type: EnumLike<typeof OptimizationActionType.PLAN_PRICE_ADJUSTMENT>;
  adjustment_type: EnumLike<typeof OptimizationPriceAdjustmentType>;
  adjustment_value: number;
};

type OptimizationActionPlanChange = AbstractOptimizationAction & {
  type: EnumLike<typeof OptimizationActionType.PLAN_CHANGE>;
  plans: {
    current_plan: string;
    target_plan: string;
  }[];
};

export type OptimizationAction =
  | OptimizationActionPriceAdjustment
  | OptimizationActionPlanChange;

export enum MidCycleBillingEnum {
  DISABLE_PRORATION = 'disable_proration',
  ENABLE_PRORATION = 'enable_proration',
}

export enum ScheduledChangesHandlingEnum {
  SKIP_CONFLICTING_SUBSCRIBERS = 'skip_conflicting_subscribers',
  DISCARD_EXISTING_SCHEDULES = 'discard_existing_schedules',
  RETAIN_EXISTING_SCHEDULES = 'retain_existing_schedules',
}

export type OptimizationConfigurations = {
  mid_cycle_billing?: MidCycleBillingEnum;
  scheduled_changes_handling?: ScheduledChangesHandlingEnum;
};

export type CustomizableOptimization = {
  display_name: string;
  audience?: RuleTopLevelCondition;
  actions?: OptimizationAction[];
  configurations?: OptimizationConfigurations;
  active?: boolean;
};

export type Optimization = {
  type: OptimizerEntityType.OPTIMIZATION;
  version: string;
  app_key: string;
  opt_id: string;
  created_at: DateString;
  updated_at: DateString;
  active: boolean;
  status?: string;
} & CustomizableOptimization;

export type OptimizationListResponse = {
  collection: Optimization[];
};

export type CreateOptimizationResponse = { ok: boolean } & Pick<
  Optimization,
  'app_key' | 'opt_id'
>;

export type DeleteOptimizationResponse = {
  ok: boolean;
} & Pick<Optimization, 'app_key' | 'opt_id'>;

type UpdateOptimizationResponse = { ok: boolean } & Optimization;

export type SubscriptionItem = {
  item_price_id: string;
  item_type: string;
  amount: number;
  quantity: number;
  billing_period: number;
  billing_cycles: number;
  unit_price?: number;
  free_quantity?: number;
  [key: string]: unknown;
};

export type SubscriptionWithChanges = {
  subscription_id: string;
  customer_id: string;
  customer: ChargebeeCustomer; // Customer data is now included directly in the subscription response
  current_subscription_items: SubscriptionItem[];
  [key: string]: unknown;
};

export type ChargebeeCustomer = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  [key: string]: unknown;
};

export type GetSubscriptionsWithChangesResponse = {
  subscriptions: SubscriptionWithChanges[];
};

export type GetCustomersForSubscriptionsResponse = {
  customers: Record<string, ChargebeeCustomer>;
};

export type GetChangesForSubscriptionResponse = {
  changes_scheduled_at: number;
  billing_alignment:
    | 'immediately'
    | 'end_of_term'
    | 'specific_date'
    | 'billing_cycles';
  scheduled_subscription_items: SubscriptionItem[];
};

export type PeriodUnit = 'DAY' | 'WEEK' | 'MONTH' | 'YEAR';

type Plan = {
  id: string;
  external_name: string; // short display name
  name: string; // full display name, includes: id + currency + frequency
  currency_code: string;
  period: number;
  period_unit: PeriodUnit;
  price: number;
};

export type GetPlansResponse = {
  total: number;
  collection: Plan[];
};

export type OptimizedSubscription = {
  subscription_id: string;
  customer_id: string;
  customer_first_name: string;
  customer_last_name: string;
  plan_id: string;
  original_item_price_id: string;
  new_item_price_id: string;
  subscription_started_at: number;
  has_scheduled_changes: boolean;
  status: string;
  currency: string;
  auto_collection: boolean | null;
  original_item_price: number;
  new_item_price: number;
  update_effective_date: string;
  billing_frequency: string;
  original_arr: number;
  new_arr: number;
  sub_plan_unit_price: number; // fallback price when original/new item prices are null
  sub_plan_quantity: number; // quantity for the subscription
  // Anticipated changes to future changes on the sub
  future_action_type?: string | null;
  future_original_unit_price?: number | null;
  future_new_unit_price?: number | null;
  future_original_item_price_id?: string | null;
  future_new_item_price_id?: string | null;
  future_original_plan_quantity?: number | null;
  future_new_plan_quantity?: number | null;
  future_reason?: string | null;
  // Snapshot of the subscription state after scheduled changes take effect (from future_sub)
  future_sub_status?: string | null;
  future_sub_plan_id?: string | null;
  future_sub_plan_amount?: number | null;
  future_sub_plan_quantity?: number | null;
  future_sub_plan_unit_price?: number | null;
  future_sub_billing_period?: number | null;
  future_sub_billing_period_unit?: string | null;
  future_sub_currency_code?: string | null;
  future_sub_item_price_id?: string | null;
  future_sub_item_unit_price?: number | null;
  future_sub_item_plan_amount?: number | null;
  future_sub_item_plan_quantity?: number | null;
  // Boolean update outcome columns (one pair non-null per job type)
  will_update_sub?: boolean | null;
  will_update_future_sub?: boolean | null;
  did_update_sub?: boolean | null;
  did_update_future_sub?: boolean | null;
};

export type GetSimulationSummaryResponse = {
  job_id: string;
  total_subs: number;
  modify_estimate: number;
  skip_estimate: number;
};

export type GetSimulationResultsResponse = {
  job_id: string;
  subscription_updates: OptimizedSubscription[];
};

export type SimulationStateResponse = {
  optimization_state: {
    job: OptimizerJob;
    result: GetSimulationResultsResponse | null;
  };
  summary: GetSimulationSummaryResponse | null;
};

export type GetExecutionSummaryResponse = {
  total_jobs: number;
  arr_impact: number;
  impacted_subscriptions: number;
};

export type GetExecutionResultsResponse = {
  job_id: string;
  subscription_updates: OptimizedSubscription[];
  pagination?: Pagination;
};

export type ExecutionStateResponse = {
  job: OptimizerJob;
  result: {
    job_id: string;
    subscription_updates: OptimizedSubscription[];
  } | null;
};

// API argument types
type GetOptimizationsArgs = { appKey: string };
type GetOptimizationArgs = { appKey: string; optId: string };
type CreateOptimizationArgs = {
  appKey: string;
} & Partial<CustomizableOptimization>;
type DeleteOptimizationArgs = { appKey: string; optId: string };
type UpdateOptimizationArgs = {
  appKey: string;
  optId: string;
} & Partial<CustomizableOptimization>;
type GetSubscriptionsWithChangesArgs = {
  appKey: string;
  audience: RuleTopLevelCondition;
};
type GetCustomersForSubscriptionsArgs = {
  appKey: string;
  customerIds: string[];
};
type GetChangesForSubscriptionArgs = {
  appKey: string;
  subId: string;
};
type GetPlansArgs = {
  appKey: string;
};
type CreateJobArgs = {
  appKey: string;
  optId: string;
  jobType: 'export' | 'simulate' | 'execute';
  cacheKey?: string;
};
type GetSimulationSummaryArgs = {
  appKey: string;
  jobId: string;
};
type GetSimulationResultsArgs = {
  appKey: string;
  jobId: string;
  page?: number;
  pageSize?: number;
};
type GetExecutionSummaryArgs = {
  appKey: string;
};
type GetExecutionResultsArgs = {
  appKey: string;
  jobId: string;
  page?: number;
  pageSize?: number;
};
type GetSimulationArgs = {
  appKey: string;
  optId: string;
};
type GetExecutionArgs = {
  appKey: string;
  optId: string;
};
type GetSimulationExportUrlArgs = {
  appKey: string;
  optId: string;
};
type GetExecutionExportUrlArgs = {
  appKey: string;
  optId: string;
};

export type CreateJobResponse = {
  ok: boolean;
  job_id: string;
  job_type: string;
};

export type GetSimulationExportUrlResponse = {
  url: string;
};

export type GetExecutionExportUrlResponse = {
  url: string;
};

export type OptimizerJob = {
  app_key: string;
  opt_id: string;
  job_id: string;
  job_ref_id?: string;
  job_type: EnumLike<typeof OptimizerJobType>;
  display_name?: string;
  created_at: DateString;
  updated_at: DateString;
  status?: EnumLike<typeof OptimizerJobStatus>;
  result?: string;
  cache_key?: string;
};

export type GetJobsResponse = {
  collection: OptimizerJob[];
};

type GetJobsArgs = {
  appKey: string;
  optId: string;
};

export class OptimizationSliceHelper {
  static cleanupActionFields(
    draft: WritableDraft<Partial<OptimizationAction>>
  ) {
    if (draft.type === OptimizationActionType.PLAN_CHANGE) {
      const d = draft as WritableDraft<
        Partial<OptimizationActionPriceAdjustment>
      >;
      delete d.adjustment_type;
      delete d.adjustment_value;
    } else {
      const d = draft as WritableDraft<Partial<OptimizationActionPlanChange>>;
      delete d.plans;
    }
  }

  static createNewOptimization() {
    const newOptimization: Partial<Optimization> = {
      display_name: 'Renewal Optimization',
      audience: this.createNewAudience(),
      configurations: this.createNewConfigurations(),
    };
    return newOptimization;
  }

  static createNewAudience(): RuleTopLevelCondition {
    const newAudience = {
      all: [
        {
          fact: '',
          operator: '',
          value: '',
        },
      ],
    };

    return newAudience;
  }

  static createEmptyAudience(): RuleTopLevelCondition {
    const newAudience = {
      all: [],
    };

    return newAudience;
  }

  static createNewAction(): OptimizationAction {
    return {
      display_name: 'New rule',
      condition: this.createNewAudience(),
      type: OptimizationActionType.PLAN_PRICE_ADJUSTMENT,
      adjustment_type: OptimizationPriceAdjustmentType.INCREASE_BY_PERCENTAGE,
      adjustment_value: 10,
    };
  }

  static createNewSimpleAction(): Partial<OptimizationAction> {
    return {
      display_name: 'Default rule',
      condition: this.createEmptyAudience(),
    };
  }

  static createNewConfigurations(): OptimizationConfigurations {
    return {
      mid_cycle_billing: MidCycleBillingEnum.DISABLE_PRORATION,
      scheduled_changes_handling:
        ScheduledChangesHandlingEnum.SKIP_CONFLICTING_SUBSCRIBERS,
    };
  }

  static safeParse<T = unknown>(text: string, fallback?: T): T | undefined {
    if (!text) return fallback;
    try {
      return JSON.parse(text) as T;
    } catch (err) {
      GlobalLogger.error(err);
      return fallback;
    }
  }

  static createNewPlanChangePlan(): OptimizationActionPlanChange['plans'][number] {
    return {
      current_plan: '',
      target_plan: '',
    };
  }

  /**
   * Generates a deterministic MD5 hash cache key from optimization's audience, actions, and configurations.
   * This is used to detect when inputs have changed and a new job is needed.
   * @param optimization - The optimization object containing audience, actions, and configurations
   * @returns MD5 hash string of the combined inputs
   */
  static generateCacheKey(optimization: Partial<Optimization>): string {
    // Serialize each field separately in a fixed order to ensure deterministic output
    // This approach is more reliable than relying on object key ordering
    const audienceStr = JSON.stringify(optimization.audience ?? null);
    const actionsStr = JSON.stringify(optimization.actions ?? null);
    const configurationsStr = JSON.stringify(
      optimization.configurations ?? null
    );

    // Combine in fixed order
    const combinedString = `${audienceStr}|${actionsStr}|${configurationsStr}`;

    // Compute MD5 hash
    return md5(combinedString);
  }

  /**
   * Generates a unique display name for duplication by checking against existing optimizations.
   * If the original name is "Name", it will generate "Name (1)".
   * If the original name is already "Name (1)", it will generate "Name (2)".
   * Continues incrementing until a unique name is found.
   * @param originalName - The original display name to duplicate
   * @param existingOptimizations - Array of existing optimizations to check against
   * @returns A unique display name that doesn't conflict with existing optimizations
   */
  static generateUniqueDisplayName(
    originalName: string,
    existingOptimizations: Optimization[]
  ): string {
    if (!existingOptimizations || existingOptimizations.length === 0) {
      return `${originalName} (1)`;
    }

    const allDisplayNames = new Set(
      existingOptimizations.map((opt) => opt.display_name?.toLowerCase() || '')
    );

    // Check if the name matches pattern "Name (N)" where N is a number at the end
    const match = originalName.match(/^(.+?)\s+\((\d+)\)$/);
    let baseName: string;
    let startNumber: number;

    if (match) {
      // If it's already "Name (N)", use the base name and start from N+1
      baseName = match[1]!.trim();
      startNumber = parseInt(match[2]!, 10) + 1;
    } else {
      // If it's just "Name", use it as base and start from 1
      baseName = originalName;
      startNumber = 1;
    }

    // Find the first available number
    let number = startNumber;
    let candidateName: string;
    do {
      candidateName = `${baseName} (${number})`;
      number++;
    } while (allDisplayNames.has(candidateName.toLowerCase()));

    return candidateName;
  }
}

export const optimizationsSlice = bbkApiSlice.injectEndpoints({
  endpoints: (build) => ({
    getOptimizations: build.query<
      OptimizationListResponse,
      GetOptimizationsArgs
    >({
      query: ({ appKey }) => `/api/v2/apps/${appKey}/optimizations`,
      providesTags: ['Optimizations'],
    }),
    getOptimization: build.query<Optimization, GetOptimizationArgs>({
      query: ({ appKey, optId }) =>
        `/api/v2/apps/${appKey}/optimizations/${optId}`,
      transformResponse: (
        raw: Omit<Optimization, 'audience' | 'actions' | 'configurations'> & {
          audience: string;
          actions: string;
          configurations: string;
        }
      ): Optimization => ({
        ...raw,
        actions: OptimizationSliceHelper.safeParse<Optimization['actions']>(
          raw.actions
        ),
        audience: OptimizationSliceHelper.safeParse<Optimization['audience']>(
          raw.audience,
          OptimizationSliceHelper.createNewAudience()
        ),
        configurations: OptimizationSliceHelper.safeParse<
          Optimization['configurations']
        >(raw.configurations),
      }),
      providesTags: (result, error, { optId }) => [
        { type: 'Optimizations', id: optId },
      ],
    }),
    createOptimization: build.mutation<
      CreateOptimizationResponse,
      CreateOptimizationArgs
    >({
      query: ({ appKey, ...body }) => ({
        url: `/api/v2/apps/${appKey}/optimizations`,
        method: 'POST',
        body: {
          ...body,
          audience: JSON.stringify(body.audience),
          actions: JSON.stringify(body.actions),
          configurations: JSON.stringify(body.configurations),
        },
      }),
      invalidatesTags: ['Optimizations'],
    }),
    deleteOptimization: build.mutation<
      DeleteOptimizationResponse,
      DeleteOptimizationArgs
    >({
      query: ({ appKey, optId }) => ({
        url: `/api/v2/apps/${appKey}/optimizations/${optId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { optId }) => [
        'Optimizations',
        { type: 'Optimizations', id: optId },
      ],
    }),
    updateOptimization: build.mutation<
      UpdateOptimizationResponse,
      UpdateOptimizationArgs
    >({
      query: ({ appKey, optId, ...body }) => ({
        url: `/api/v2/apps/${appKey}/optimizations/${optId}`,
        method: 'PUT',
        body: {
          ...body,
          audience: body.audience ? JSON.stringify(body.audience) : undefined,
          actions: body.actions ? JSON.stringify(body.actions) : undefined,
          configurations: body.configurations
            ? JSON.stringify(body.configurations)
            : undefined,
        },
      }),
      invalidatesTags: (result, error, { optId }) => [
        'Optimizations',
        { type: 'Optimizations', id: optId },
      ],
    }),
    getSubscriptionsWithChanges: build.query<
      GetSubscriptionsWithChangesResponse,
      GetSubscriptionsWithChangesArgs
    >({
      query: ({ appKey, audience }) => ({
        url: `/api/v2/apps/${appKey}/optimizations/load-conflicting-subscriptions`,
        method: 'POST',
        body: {
          audience: JSON.stringify(audience),
        },
      }),
    }),
    getCustomersForSubscriptions: build.query<
      GetCustomersForSubscriptionsResponse,
      GetCustomersForSubscriptionsArgs
    >({
      query: ({ appKey, customerIds }) => ({
        url: `/api/v2/apps/${appKey}/optimizations/preview-conflicting-subscriptions`,
        method: 'POST',
        body: {
          customer_ids: JSON.stringify(customerIds),
        },
      }),
    }),
    getChangesForSubscription: build.query<
      GetChangesForSubscriptionResponse,
      GetChangesForSubscriptionArgs
    >({
      query: ({ appKey, subId }) =>
        `/api/v2/apps/${appKey}/optimizations/preview-conflicting-subscriptions/details?sub_id=${subId}`,
    }),
    getPlans: build.query<GetPlansResponse['collection'], GetPlansArgs>({
      query: ({ appKey }) =>
        `/api/v2/apps/${appKey}/optimizations-chargebee/plans`,
      transformResponse: (res: GetPlansResponse) => res.collection,
    }),
    createJob: build.mutation<CreateJobResponse, CreateJobArgs>({
      query: ({ appKey, optId, jobType, cacheKey }) => ({
        url: `/api/v2/apps/${appKey}/optimizations/${optId}/jobs`,
        method: 'POST',
        body: {
          job_type: jobType,
          ...(cacheKey && { cache_key: cacheKey }),
        },
      }),
    }),
    getJobs: build.query<GetJobsResponse, GetJobsArgs>({
      query: ({ appKey, optId }) =>
        `/api/v2/apps/${appKey}/optimizations/${optId}/jobs`,
    }),
    getSimulationSummary: build.query<
      GetSimulationSummaryResponse,
      GetSimulationSummaryArgs
    >({
      query: ({ appKey, jobId }) =>
        `/api/v2/apps/${appKey}/optimizations/simulations/${jobId}/summary`,
    }),
    getSimulationResults: build.query<
      GetSimulationResultsResponse,
      GetSimulationResultsArgs
    >({
      query: ({ appKey, jobId, page, pageSize }) => {
        const params = new URLSearchParams();
        if (page !== undefined) params.append('page', page.toString());
        if (pageSize !== undefined)
          params.append('page_size', pageSize.toString());
        const queryString = params.toString();
        return `/api/v2/apps/${appKey}/optimizations/simulations/${jobId}${queryString ? `?${queryString}` : ''}`;
      },
    }),
    getSimulation: build.query<SimulationStateResponse, GetSimulationArgs>({
      query: ({ appKey, optId }) =>
        `/api/v2/apps/${appKey}/optimizations/${optId}/simulation`,
    }),
    getExecution: build.query<ExecutionStateResponse, GetExecutionArgs>({
      query: ({ appKey, optId }) =>
        `/api/v2/apps/${appKey}/optimizations/${optId}/execution`,
    }),
    getSimulationExportUrl: build.query<
      GetSimulationExportUrlResponse,
      GetSimulationExportUrlArgs
    >({
      query: ({ appKey, optId }) =>
        `/api/v2/apps/${appKey}/optimizations/${optId}/simulation/export`,
    }),
    getExecutionExportUrl: build.query<
      GetExecutionExportUrlResponse,
      GetExecutionExportUrlArgs
    >({
      query: ({ appKey, optId }) =>
        `/api/v2/apps/${appKey}/optimizations/${optId}/execution/export`,
    }),
    getExecutionSummary: build.query<
      GetExecutionSummaryResponse,
      GetExecutionSummaryArgs
    >({
      query: ({ appKey }) => `/api/v2/apps/${appKey}/optimizations/executions`,
    }),
    getExecutionResults: build.query<
      GetExecutionResultsResponse,
      GetExecutionResultsArgs
    >({
      query: ({ appKey, jobId, page, pageSize }) => {
        const params = new URLSearchParams();
        if (page !== undefined) params.append('page', page.toString());
        if (pageSize !== undefined)
          params.append('page_size', pageSize.toString());
        const queryString = params.toString();
        return `/api/v2/apps/${appKey}/optimizations/executions/${jobId}${queryString ? `?${queryString}` : ''}`;
      },
    }),
  }),
});
